/**
 * BSE StAR MF API - Frontend helper
 * Calls backend /api/bse/* which proxies to BSE StAR MF (APIFileStructure.pdf)
 */
import axios from 'axios';

/**
 * BSE routes are called against your Node proxy. Prefer apex host when .env
 * still points at www.gpsfinvest.com (matches https://gpsfinvest.com/api/...).
 * Override with REACT_APP_BSE_API_BASE_URL if needed.
 */
function resolveApiBaseUrl() {
  const bseOnly = String(process.env.REACT_APP_BSE_API_BASE_URL || '').trim();
  if (bseOnly) return bseOnly.replace(/\/+$/, '');
  const raw = String(process.env.REACT_APP_BACKEND_BASE_URL || '').trim();
  return raw
    .replace(/^https?:\/\/www\.gpsfinvest\.com(?=\/|$)/i, 'https://gpsfinvest.com')
    .replace(/\/+$/, '');
}

const BASE = resolveApiBaseUrl();

/**
 * BSE portal login - validate credentials and get portal URL (opens in new tab from BseState)
 * GET /api/bse/portal/login
 */
export const getPortalLogin = () =>
  axios.get(`${BASE}/bse/portal/login`);

/**
 * Get Order API session (valid 1 hour) - call before placing orders
 */
export const getOrderSession = () =>
  axios.post(`${BASE}/bse/auth/order`);

/**
 * Get scheme list for Lumpsum/SIP dropdown
 */
export const getSchemes = () =>
  axios.get(`${BASE}/bse/schemes`);

/**
 * Get SIP registrations from database
 * @param {Object} params - { userId?: number, clientCode?: string }
 */
export const getSipList = (params = {}) =>
  axios.get(`${BASE}/bse/sips`, { params });

/**
 * Get Additional Services session (valid 5 min)
 */
export const getAdditionalSession = () =>
  axios.post(`${BASE}/bse/auth/additional`);

/**
 * Change BSE StAR MF Password - StarMFCommonAPI Member/ChangePassword
 * @param {Object} data - { oldPassword, newPassword }
 */
export const changePassword = (data) =>
  axios.post(`${BASE}/bse/auth/change-password`, data);

/**
 * Lumpsum Purchase or Redemption
 * @param {Object} data - { encryptedPassword, passKey?, clientCode, schemeCd, buySell, amount, ... }
 */
export const lumpsumOrder = (data) =>
  axios.post(`${BASE}/bse/order/lumpsum`, data);

/**
 * SIP Order
 * @param {Object} data - { encryptedPassword, clientCode, schemeCd, startDate, installmentAmount, noOfInstallments, ... }
 */
export const sipOrder = (data) =>
  axios.post(`${BASE}/bse/order/sip`, data);

/**
 * XSIP / ISIP Order (mandate-based)
 * @param {Object} data - { encryptedPassword, passKey?, clientCode, schemeCd, xsipMandateId, startDate, installmentAmount, noOfInstallments, euin, ... }
 */
export const xsipOrder = (data) =>
  axios.post(`${BASE}/bse/order/xsip`, data);

/**
 * SIP Cancel
 * @param {Object} data - { encryptedPassword, passKey?, clientCode, schemeCd, regId }
 */
export const sipCancelOrder = (data) =>
  axios.post(`${BASE}/bse/order/sip-cancel`, data);

/**
 * SIP to XSIP Shift
 * @param {Object} data - { regId, clientCode, schemeCd, xsipMandateId, effectiveDate }
 */
export const sipToXsipShift = (data) =>
  axios.post(`${BASE}/bse/sip/to-xsip-shift`, data);

/**
 * SIP / XSIP Pause-Resume
 * @param {Object} data - { regId, clientCode, schemeCd, action, fromDate?, toDate? }
 * action: "P" = Pause, "R" = Resume
 */
export const sipPauseResume = (data) =>
  axios.post(`${BASE}/bse/sip/pause-resume`, data);

/**
 * Switch Order
 * @param {Object} data - { encryptedPassword, clientCode, fromSchemeCd, toSchemeCd, switchAmount, ... }
 */
export const switchOrder = (data) =>
  axios.post(`${BASE}/bse/order/switch`, data);

/**
 * Spread Order (Overnight / Bulk Order)
 * @param {Object} data - { encryptedPassword, passKey?, clientCode, schemeCd, buySellType, dptxn, orderVal, execDate, folioNo? }
 */
export const spreadOrder = (data) =>
  axios.post(`${BASE}/bse/order/spread`, data);

/**
 * Mandate Registration (e-NACH / OTM / XSIP) - MFAPI Flag 06
 * @param {Object} data - { clientCode, accountNo, ifscCode, bankName, mandateAmount, accountType, fromDate, toDate, frequency, returnUrl? }
 */
export const mandateRegistration = (data) =>
  axios.post(`${BASE}/bse/mandate/register`, data);

/**
 * Mandate Scan Image Upload - MFAPI Flag 08
 * @param {Object} data - { mandateId, clientCode, fileName, fileType, base64Data }
 */
export const mandateUpload = (data) =>
  axios.post(`${BASE}/bse/mandate/upload`, data);

/**
 * AOF Image Upload - MFAPI Flag 09
 * @param {Object} data - { clientCode, pan, fileName, fileType, base64Data }
 */
export const aofUpload = (data) =>
  axios.post(`${BASE}/bse/aof/upload`, data);

/**
 * Cheque Image Upload - MFAPI Flag 10
 * @param {Object} data - { clientCode, pan, accountHolderType, fileName, fileType, base64Data }
 * accountHolderType: N=Normal, NRI=NRI, M=Minor
 */
export const chequeUpload = (data) =>
  axios.post(`${BASE}/bse/cheque/upload`, data);

/**
 * Mandate Shift - MFAPI Flag 22
 * @param {Object} data - { regId, clientCode, schemeCd, oldMandateId, newMandateId, effectiveDate }
 */
export const mandateShift = (data) =>
  axios.post(`${BASE}/bse/mandate/shift`, data);

/**
 * Mandate Status (List) - MandateDetailsRequest
 * @param {Object} data - { fromDate?, toDate?, clientCode, mandateId? }
 */
export const getMandateStatus = (data) =>
  axios.post(`${BASE}/bse/mandate/status`, data);

/**
 * Mandate Status (MFAPI Flag 07) - Single mandate
 * @param {Object} data - { mandateId, clientCode }
 */
export const getMandateStatusSingle = (data) =>
  axios.post(`${BASE}/bse/mandate/status/single`, data);

/**
 * Child Orders (SIP/XSIP/ISIP/STP/SWP)
 * @param {Object} data - { date?, clientCode, systematicPlanType, regnNo }
 */
export const getChildOrders = (data) =>
  axios.post(`${BASE}/bse/orders/child`, data);

/**
 * Enhanced UCC Registration - BSE StAR MF ClientMaster/Registration
 * @param {Object} data - { userId, ClientName, DOB, Gender, ... } per APIFileStructure.pdf
 */
export const uccRegistration = (data) =>
  axios.post(`${BASE}/bse/client/ucc-registration`, data);

/**
 * UCC / Client Registration
 * @param {Object} data - Full client JSON per BSE structure
 */
export const registerClient = (data) =>
  axios.post(`${BASE}/bse/client/register`, data);

/**
 * FATCA Upload - MFUploadService MFAPI Flag 01
 * @param {Object} data - { clientCode, PAN, clientName, DOB, taxStatus, countryCode, occupationCode, sourceOfWealth, applicantIncome, taxResidencyCountry, uboFlag, uboCode? }
 */
export const fatcaUpload = (data) =>
  axios.post(`${BASE}/bse/fatca/upload`, data);

/**
 * CKYC Upload - MFUploadService MFAPI Flag 13
 * @param {Object} data - { clientCode, PAN, ckycNo, documentType, documentContent (Base64) }
 */
export const ckycUpload = (data) =>
  axios.post(`${BASE}/bse/ckyc/upload`, data);

/**
 * Payment Gateway (MFAPI Flag 03) - Payment Mode Selection
 * @param {Object} data - { orderNo, clientCode, amount, paymentMode, bankCode?, upiId?, returnUrl? }
 * paymentMode: NB=Net Banking, UPI=UPI, NEFT=NEFT/RTGS
 */
export const paymentGateway = (data) =>
  axios.post(`${BASE}/bse/payment/gateway`, data);

/**
 * Payment Status
 * @param {Object} data - { clientCode, orderNo, segment? }
 */
export const getPaymentStatus = (data) =>
  axios.post(`${BASE}/bse/payment/status`, data);

/**
 * Order Status - MFAPI Flag 01
 * @param {Object} data - { clientCode, orderNo? }
 * Single order: pass orderNo. All orders: omit orderNo.
 */
export const getOrderStatus = (data) =>
  axios.post(`${BASE}/bse/order/status`, data);

/**
 * Allotment Statement - MFAPI Flag 02
 * @param {Object} data - { clientCode, fromDate, toDate, schemeCd? }
 * Dates in DD/MM/YYYY format
 */
export const getAllotmentStatement = (data) =>
  axios.post(`${BASE}/bse/order/allotment-statement`, data);

/**
 * Redemption Statement - MFAPI Flag 04
 * @param {Object} data - { clientCode, fromDate, toDate, schemeCd? }
 * Dates in DD/MM/YYYY format
 */
export const getRedemptionStatement = (data) =>
  axios.post(`${BASE}/bse/order/redemption-statement`, data);

/**
 * Child Order Status - MFAPI Flag 05
 * @param {Object} data - { orderNo, clientCode }
 */
export const getChildOrderStatus = (data) =>
  axios.post(`${BASE}/bse/order/child-status`, data);

/**
 * Order Rejection - MFAPI Flag 12
 * @param {Object} data - { orderNo, clientCode }
 */
export const getOrderRejection = (data) =>
  axios.post(`${BASE}/bse/order/rejection`, data);

/**
 * Submit mutual fund investor registration to BSE (proxied by backend).
 * @param {Object} data - request body expected by the server normalizer
 */
export const submitMutualFundInvestorProfile = (data) =>
  axios.post(`${BASE}/bse/v2/add_ucc`, data);

/** @deprecated Prefer {@link submitMutualFundInvestorProfile}. */
export const addUccV2 = submitMutualFundInvestorProfile;

/**
 * BSE v2 Master Scheme List - fetch mutual fund scheme master
 * @param {Object} data - { fields, count_only, start, length, search, filter_param }
 */
export const masterSchemeList = (data = {}) =>
  axios.post(`${BASE}/bse/master_scheme_list`, data);

/**
 * BSE v2 List UCC — paginated UCC list for member (admin).
 * @param {Object} data - { data: { member, fields, count_only, start, length, ucc_status?, investor?, filter_param? } }
 */
export const listUccV2 = (data = {}) =>
  axios.post(`${BASE}/bse/v2/list_ucc`, data);

/**
 * BSE v2 Get UCC — fetch single investor UCC details.
 * @param {Object} data - { data: { member: { member_id }, investor: { client_code } } }
 */
export const getUccV2 = (data = {}) =>
  axios.post(`${BASE}/bse/v2/get_ucc`, data);

/**
 * BSE v2 Update UCC — partial update (comm_addr, holder/contact, etc.).
 * @param {Object} data - { data: { member, investor, comm_addr?, holder?, ... } }
 */
export const updateUccV2 = (data = {}) =>
  axios.post(`${BASE}/bse/v2/update_ucc`, data);

/**
 * BSE v2 Get KYC Link — pending KYC / authentication URL for investor.
 * @param {Object} data - { data: [{ event, investor: { client_code }, member_code }] }
 */
export const getKycLinkV2 = (data = {}) =>
  axios.post(`${BASE}/bse/v2/get_kyc_link`, data);

/**
 * BSE v2 Get 2FA Link — nominee / bank / PAN 2FA flows.
 * @param {Object} data - same shape as get_kyc_link
 */
export const get2faLinkV2 = (data = {}) =>
  axios.post(`${BASE}/bse/v2/get_2fa_link`, data);

/**
 * BSE verification link — server auto-selects KYC vs 2FA (kyc_type K uses 2FA).
 * @param {Object} data - { data: [{ event, investor: { client_code }, member_code }] }
 */
export const getVerificationLinkV2 = (data = {}) =>
  axios.post(`${BASE}/bse/v2/get_verification_link`, data);

/**
 * BSE v2 Place new order — purchase / redeem / switch.
 * @param {Object} data - { data: { orders: [...] } }
 */
export const orderNew = (data = {}) => axios.post(`${BASE}/bse/order_new`, data);

/**
 * BSE v2 List orders (reports).
 * @param {Object} data - { data: { fields, start, length, filter_param } }
 */
export const orderList = (data = {}) => axios.post(`${BASE}/bse/order_list`, data);

/**
 * BSE v2 Get single order by BSE order id.
 * @param {Object} data - { data: { id, open_close? } }
 */
export const orderGet = (data = {}) => axios.post(`${BASE}/bse/order_get`, data);

/**
 * BSE v2 Update an existing order (modification before settlement).
 * @param {Object} data - { data: { orders: [{ id, type, scheme, amount, investor, member, ... }] } }
 */
export const orderUpdate = (data = {}) => axios.post(`${BASE}/bse/order_update`, data);

/**
 * BSE v2 Cancel an order by BSE order id.
 * @param {Object} data - { data: { id } }
 */
export const orderCancel = (data = {}) => axios.post(`${BASE}/bse/order_cancel`, data);

/**
 * BSE v2 Register mandate (eNACH / NACH / UPI AutoPay).
 * @param {Object} data - { data: { investor, member, investor_bank_details, amount, ... } }
 */
export const mandateRegisterV2 = (data = {}) =>
  axios.post(`${BASE}/bse/mandate_register`, data);

/** BSE v2 List mandates for a UCC. */
export const mandateListV2 = (data = {}) => axios.post(`${BASE}/bse/mandate_list`, data);

/** BSE v2 Get mandate by exch_mandate_id. */
export const mandateGetV2 = (data = {}) => axios.post(`${BASE}/bse/mandate_get`, data);

/** BSE v2 Cancel mandate by id. */
export const mandateCancelV2 = (data = {}) => axios.post(`${BASE}/bse/mandate_cancel`, data);

/**
 * BSE v2 Get payment detail — pass exactly one of order_id, payment_ref_id, bank_txn_ref.
 */
export const getPaymentDetail = (data = {}) =>
  axios.post(`${BASE}/bse/get_payment_detail`, data);

/**
 * BSE v2 List payment details — max 50 items in data array.
 */
export const listPaymentDetail = (data = {}) =>
  axios.post(`${BASE}/bse/list_payment_detail`, data);
